import { useEffect, useRef, useCallback, useState } from 'react';
import { toast } from 'sonner';
import { getUnreadCount } from '@/lib/api';

// Notification sound - simple beep using Web Audio API
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (e) {
    console.log('Could not play notification sound:', e);
  }
};

export const useWebSocket = (token, onUnreadCountUpdate) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [usePolling, setUsePolling] = useState(false);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 3;
  const lastUnreadCount = useRef(0);
  
  // Polling fallback when WebSocket is not available
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return;
    
    console.log('Starting polling fallback for notifications');
    setUsePolling(true);
    
    const poll = async () => {
      try {
        const data = await getUnreadCount();
        const newCount = data.count;
        
        // If count increased, show notification
        if (newCount > lastUnreadCount.current && lastUnreadCount.current > 0) {
          playNotificationSound();
          toast('Nueva notificación', {
            description: 'Tienes nuevas notificaciones',
            duration: 3000,
          });
        }
        
        lastUnreadCount.current = newCount;
        if (onUnreadCountUpdate) {
          onUnreadCountUpdate(newCount);
        }
      } catch (e) {
        console.error('Polling error:', e);
      }
    };
    
    // Initial fetch
    poll();
    
    // Poll every 15 seconds
    pollingIntervalRef.current = setInterval(poll, 15000);
  }, [onUnreadCountUpdate]);
  
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setUsePolling(false);
  }, []);
  
  const connect = useCallback(() => {
    if (!token) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    // Determine WebSocket URL based on current location
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:\/\//, '') || window.location.host;
    const wsUrl = `${protocol}//${host}/ws/notifications?token=${token}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      // Set timeout for connection
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          console.log('WebSocket connection timeout, falling back to polling');
          ws.close();
          startPolling();
        }
      }, 5000);
      
      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        console.log('WebSocket connected');
        setIsConnected(true);
        reconnectAttempts.current = 0;
        stopPolling();
      };
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === 'init') {
            // Initial connection - update unread count
            if (onUnreadCountUpdate && message.data?.unread_count !== undefined) {
              lastUnreadCount.current = message.data.unread_count;
              onUnreadCountUpdate(message.data.unread_count);
            }
          } else if (message.type === 'notification') {
            const { data } = message;
            
            // Update unread count (+1)
            if (onUnreadCountUpdate) {
              onUnreadCountUpdate(prev => {
                const newCount = typeof prev === 'number' ? prev + 1 : 1;
                lastUnreadCount.current = newCount;
                return newCount;
              });
            }
            
            // Play sound if enabled
            if (data.play_sound) {
              playNotificationSound();
            }
            
            // Show toast if enabled
            if (data.show_toast) {
              toast(data.title, {
                description: data.message,
                action: data.link ? {
                  label: 'Ver',
                  onClick: () => {
                    window.location.href = data.link;
                  }
                } : undefined,
                duration: 5000,
              });
            }
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };
      
      ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        console.log('WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        
        // If connection failed immediately or auth error, use polling
        if (event.code === 1006 || event.code >= 4000) {
          console.log('WebSocket unavailable, using polling');
          startPolling();
          return;
        }
        
        // Attempt reconnect for other cases
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 10000);
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        } else if (reconnectAttempts.current >= maxReconnectAttempts) {
          console.log('Max reconnect attempts reached, using polling');
          startPolling();
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
      // Ping every 30 seconds to keep connection alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);
      
      ws.pingInterval = pingInterval;
      
    } catch (e) {
      console.error('Error creating WebSocket:', e);
      startPolling();
    }
  }, [token, onUnreadCountUpdate, startPolling, stopPolling]);
  
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    stopPolling();
    if (wsRef.current) {
      if (wsRef.current.pingInterval) {
        clearInterval(wsRef.current.pingInterval);
      }
      wsRef.current.close(1000, 'User disconnect');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, [stopPolling]);
  
  useEffect(() => {
    if (token) {
      connect();
    } else {
      disconnect();
    }
    
    return () => {
      disconnect();
    };
  }, [token, connect, disconnect]);
  
  return { isConnected, usePolling, reconnect: connect, disconnect };
};

export default useWebSocket;
